import {
	DEFAULT_FORM_FIELDS,
	FIELD_CATALOGUE,
	FORM_FIELD_KEYS,
	type FormFieldConfig,
	type FormFieldKey,
} from "@shared/brand.types";
import { Lock } from "lucide-react";
import { useId, useState } from "react";

/**
 * Les champs que l'école demande, et lesquels sont obligatoires.
 *
 * Le catalogue est FERMÉ, et le tableau se lit donc dans son ordre à lui plutôt que
 * dans celui de la configuration enregistrée : une école qui active un champ ne
 * doit pas le voir sauter en bas de la liste.
 *
 * Deux champs portent `core` dans le catalogue et ne se décochent pas : sans
 * intitulé ni missions, une offre arrive vide et l'école ne peut rien en faire.
 * Le serveur les REFUSE aussi — un cadenas dans l'écran n'est pas une règle.
 *
 * Deux rendus, un seul état. À partir de `md`, le tableau du référentiel. En
 * dessous, une rangée par champ : le libellé sur toute la largeur, puis deux cases
 * LIBELLÉES (« Affiché », « Obligatoire ») de 44px, sur le modèle de
 * `CheckboxLabelPrimary` du proto. Le tableau à colonnes fixes laissait 120px aux
 * libellés en 390, qui passaient sur deux lignes, et des cases de 18px sans rien
 * de cliquable autour.
 */
export function FormFieldsEditor({
	value,
	onSave,
}: {
	value: FormFieldConfig[];
	onSave: (fields: FormFieldConfig[]) => Promise<boolean>;
}) {
	const [champs, setChamps] = useState<FormFieldConfig[]>(() => complet(value));
	const [etat, setEtat] = useState<"idle" | "saving" | "failed">("idle");
	// L'échec retient SA clé : `aria-invalid` posé sur tout le tableau annoncerait
	// vingt-deux cases en erreur alors qu'une seule a été refusée.
	const [enEchec, setEnEchec] = useState<FormFieldKey | null>(null);
	// `useId` et pas un identifiant écrit à la main. Un identifiant en dur est un pari
	// sur le fait que le composant ne sera jamais monté deux fois, et ce pari se perd
	// sans bruit : deux `aria-describedby` désigneraient la même rangée. Ici une seule
	// instance existe (`ConfigPage` est monté avec une `key`) : c'est une précaution,
	// pas la correction d'un défaut observé.
	const erreurId = useId();
	const aideId = useId();

	async function ecrire(key: FormFieldKey, suivants: FormFieldConfig[]) {
		const avant = champs;
		setChamps(suivants);
		setEtat("saving");
		// ⚠️ L'échec se retire AVANT l'attente, pas après. `setEtat("saving")` démonte le
		// paragraphe d'erreur, mais `enEchec` restait posé pendant tout l'aller-retour :
		// il existait donc un rendu où une case portait un `aria-describedby` désignant
		// un identifiant absent du document.
		setEnEchec(null);
		const ok = await onSave(suivants);
		if (!ok) {
			// On REVIENT en arrière : laisser la case cochée après un refus ferait
			// croire à un formulaire qui a changé, alors que le dépôt public n'a rien vu.
			setChamps(avant);
		}
		setEtat(ok ? "idle" : "failed");
		setEnEchec(ok ? null : key);
	}

	const modifier = (key: FormFieldKey, patch: Partial<FormFieldConfig>) =>
		ecrire(
			key,
			champs.map((champ) => (champ.key === key ? { ...champ, ...patch } : champ)),
		);

	/** Les deux cases d'un champ, les mêmes dans les deux rendus. */
	function cases(champ: FormFieldConfig, rendu: "liste" | "tableau") {
		const spec = FIELD_CATALOGUE[champ.key];
		// Le cadenas explique pourquoi la case ne bouge pas ; il ne sert à rien s'il
		// n'est rattaché qu'à l'œil. Un identifiant par rendu : les deux coexistent
		// dans le document, l'un masqué.
		const aide = spec.core ? `${aideId}-${rendu}-${champ.key}` : undefined;
		const echoue = enEchec === champ.key;
		const decrit = [aide, echoue ? erreurId : undefined].filter(Boolean).join(" ") || undefined;
		const commun = {
			type: "checkbox" as const,
			className: "mg-checkbox shrink-0",
			"aria-describedby": decrit,
			"aria-invalid": echoue || undefined,
		};
		const affiche = (
			<input
				{...commun}
				// Le nom contient le mot affiché à côté (« affiché ») : un utilisateur de
				// commande vocale dit ce qu'il voit.
				id={`${aideId}-${rendu}-${champ.key}-affiche`}
				aria-label={`${spec.label} : affiché`}
				checked={champ.enabled}
				disabled={spec.core || etat === "saving"}
				onChange={(event) =>
					modifier(champ.key, {
						enabled: event.target.checked,
						// Décoché, un champ ne peut plus être obligatoire : la combinaison
						// n'a pas de sens, et le dépôt l'ignorerait en silence.
						required: event.target.checked ? champ.required : false,
					})
				}
			/>
		);
		const obligatoire = (
			<input
				{...commun}
				id={`${aideId}-${rendu}-${champ.key}-obligatoire`}
				aria-label={`${spec.label} : obligatoire`}
				checked={champ.required}
				disabled={spec.core || !champ.enabled || etat === "saving"}
				onChange={(event) => modifier(champ.key, { required: event.target.checked })}
			/>
		);
		return { aide, affiche, obligatoire };
	}

	return (
		/*
		 * Les vingt-deux cases forment UN réglage, et le `fieldset` le dit. Sans
		 * lui, un lecteur d'écran les rencontre une à une sans jamais annoncer de
		 * quoi elles relèvent : la seule chose qui les rassemblait était le titre
		 * de la carte, qui vit dans le composant parent et ne les nomme pas.
		 * La légende n'est pas affichée : le titre de la carte la dit déjà à l'œil.
		 */
		<fieldset className="min-w-0">
			<legend className="sr-only">Champs demandés à l'entreprise</legend>

			{/* Sous `md` : une rangée par champ, deux cases libellées. */}
			<ul className="divide-y divide-mg-border-subtle md:hidden">
				{champs.map((champ) => {
					const spec = FIELD_CATALOGUE[champ.key];
					const { aide, affiche, obligatoire } = cases(champ, "liste");
					const libelle = `t-body flex min-h-11 items-center gap-2 text-mg-body ${
						spec.core ? "cursor-not-allowed" : "cursor-pointer"
					}`;
					return (
						<li key={champ.key} className="px-6 py-3">
							<p className="t-body-lg text-mg-body">{spec.label}</p>
							{spec.core ? <Cadenas id={aide} /> : null}
							<div className="mt-1 flex flex-wrap gap-x-6">
								<label htmlFor={`${aideId}-liste-${champ.key}-affiche`} className={libelle}>
									{affiche}
									Affiché
								</label>
								<label htmlFor={`${aideId}-liste-${champ.key}-obligatoire`} className={libelle}>
									{obligatoire}
									Obligatoire
								</label>
							</div>
						</li>
					);
				})}
			</ul>

			{/*
			 * À partir de `md` : le tableau. Le chrome vient des tokens `--mg-table-*`
			 * du référentiel, et la HIÉRARCHIE avec : au proto, l'en-tête est en 16/600
			 * couleur d'encre et la cellule en 16/400 couleur de corps.
			 *
			 * La première colonne est en `px-6`, le retrait de la carte : en `px-4`,
			 * « Champ » était décalé de 8px du titre au-dessus.
			 */}
			<table className="hidden w-full md:table">
				<thead>
					<tr className="border-b border-mg-table-head-border bg-mg-table-head">
						<th scope="col" className="t-button-lg h-table-head px-6 text-left text-mg-heading">
							Champ
						</th>
						<th
							scope="col"
							className="t-button-lg h-table-head w-24 px-2 text-center text-mg-heading"
						>
							Affiché
						</th>
						<th
							scope="col"
							className="t-button-lg h-table-head w-32 px-2 pr-6 text-center text-mg-heading"
						>
							Obligatoire
						</th>
					</tr>
				</thead>
				<tbody>
					{champs.map((champ) => {
						const spec = FIELD_CATALOGUE[champ.key];
						const { aide, affiche, obligatoire } = cases(champ, "tableau");
						return (
							<tr
								key={champ.key}
								className="h-row border-b border-mg-border-subtle transition-colors last:border-b-0 hover:bg-mg-hover"
							>
								{/*
								 * `th scope="row"` et pas `td` : c'est ce qui permet à un lecteur
								 * d'écran de dire de quel champ relève la case qu'on vient
								 * d'atteindre. Le poids et l'alignement sont remis à plat.
								 *
								 * `py-px` sur les trois cellules : c'est le px que le navigateur
								 * leur donnait sous Tailwind 3, et dont dépend la hauteur des
								 * lignes qui passent sur deux lignes de texte.
								 */}
								<th scope="row" className="px-6 py-px text-left font-normal">
									<span className="t-body-lg mr-2 text-mg-body">{spec.label}</span>
									{spec.core ? <Cadenas id={aide} /> : null}
								</th>
								<td className="px-2 py-px text-center">{affiche}</td>
								<td className="px-2 py-px pr-6 text-center">{obligatoire}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			{etat === "failed" ? (
				<p
					id={erreurId}
					role="alert"
					className="t-caption border-t border-mg-border-subtle px-6 py-3 text-mg-error-fg"
				>
					Ce réglage n'a pas été enregistré. Réessayez.
				</p>
			) : null}
		</fieldset>
	);
}

/** Pourquoi la case ne bouge pas : le champ est demandé à toutes les entreprises. */
function Cadenas({ id }: { id: string | undefined }) {
	return (
		<span id={id} className="t-caption inline-flex items-center gap-1 text-mg-secondary">
			<Lock className="size-3" aria-hidden="true" />
			Toujours demandé
		</span>
	);
}

/**
 * Complète la configuration avec les champs que l'école n'a jamais touchés.
 *
 * Une marque importée depuis main-app porte la configuration par défaut, mais rien
 * ne garantit qu'elle porte les ONZE clés : le catalogue peut gagner un champ après
 * coup. Les absents se rendent alors décochés, plutôt que de disparaître du tableau.
 */
function complet(value: FormFieldConfig[]): FormFieldConfig[] {
	const parCle = new Map(value.map((champ) => [champ.key, champ]));
	return FORM_FIELD_KEYS.map(
		(key) =>
			parCle.get(key) ??
			DEFAULT_FORM_FIELDS.find((defaut) => defaut.key === key) ?? {
				key,
				enabled: false,
				required: false,
			},
	);
}
